'use client';

import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material';
import { PropsWithChildren, useMemo } from 'react';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';

import { Theme, ThemeType } from '../../theme';
import { useColorMode } from '../ColorModeContext';
import { ColorModeContextProvider } from '../ColorModeContext/ColorModeContextProvider';

import { ThemeProviderProps } from './types';

const ThemeProviderComponent = (
  props: PropsWithChildren<{ isRtl?: boolean; theme: ThemeType }>,
) => {
  const { children, isRtl, theme } = props;
  const direction: Theme['direction'] = isRtl ? 'rtl' : 'ltr';
  const { mode } = useColorMode();
  const themeByMode =
    mode === 'dark'
      ? { ...theme['darkTheme'], direction }
      : { ...theme['lightTheme'], direction };

  return <MuiThemeProvider theme={themeByMode}>{children}</MuiThemeProvider>;
};

export const ThemeProvider = (props: ThemeProviderProps) => {
  const { mode, isRtl, ...rest } = props;

  const cache = useMemo(() => {
    if (isRtl) {
      return createCache({
        key: 'muirtl',
        stylisPlugins: [prefixer, rtlPlugin],
      });
    }

    return createCache({ key: 'css', prepend: true });
  }, [isRtl]);

  return (
    <ColorModeContextProvider mode={mode}>
      <CacheProvider value={cache}>
        <ThemeProviderComponent isRtl={isRtl} {...rest} />
      </CacheProvider>
    </ColorModeContextProvider>
  );
};
